import React from 'react';
import uniqueId from '../../utils/uniqueId';
import './MultiDigitInput.scss';

const MultiDigitInput = React.forwardRef(
    /**Renders a group of single-digit inputs representing a single string
     * @param {object} props Incoming properties set as attributes.
     * @param {number} props.digits Number of digits.
     * @param {string} props.value Value of the resultant string.
     * @param {React.Dispatch<React.SetStateAction<string>>} props.onChange State change event for the resultant string.
     * @param {string} props.label Label to display prior to digit inputs.
     * @param {boolean} props.autofocus If true, first digit input will be focused upon rendering of this component.
     * @param {boolean} props.disabled If true, this component and its input elements will be disabled.
     * @returns {JSX.Element} Multi digit input component.
     */
    ({ digits, value, onChange, label, autofocus, disabled }, ref) => {
        digits = parseInt(digits || 6);

        const items = [];
        for (let i = 0; i < digits; i++) {
            items.push({
                id: uniqueId('mdi-'),
                label: `digit ${i + 1}`,
                value: value.length >= i + 1 ? value[i] : '',
            });
        }

        const [itemEls, setItemEls] = React.useState(items);

        const keyDown = (index, event) => {
            if (event.keyCode !== 8) return;
            if (index === 0) return;
            event.target.value = '';
            const newItemEls = [...itemEls];
            newItemEls[index].value = '';
            setItemEls(newItemEls);
            const fullVal = newItemEls.map((e) => e.value).join('');
            onChange(fullVal);

            const prevElement = event.target.parentElement.parentElement.querySelectorAll('input')[index - 1];
            prevElement.focus();
        };

        /**Handle paste event on a digit Input element.
         * @param {React.ClipboardEvent<HTMLInputElement>} e Paste event
         */
        const handlePaste = (e) => {
            let pastedText = e.clipboardData.getData('text/plain').trim();
            if (pastedText.length !== digits) return;
            const newItemEls = [...itemEls];
            for (let i = 0; i < pastedText.length; i++) {
                newItemEls[i].value = pastedText[i];
            }
            setItemEls(newItemEls);
            onChange(pastedText);
            e.preventDefault();
        };

        const update = (index, event) => {
            event.target.value = event.target.value.slice(0, 1);

            const newItemEls = [...itemEls];
            newItemEls[index].value = event.target.value;
            setItemEls(newItemEls);
            const fullVal = newItemEls.map((e) => e.value).join('');
            onChange(fullVal);

            if (event.target.value.length === 0) return;
            const nextElement = event.target.parentElement.parentElement.querySelectorAll('input')[index + 1];
            if (!nextElement) return;
            nextElement.focus();
            nextElement.type = 'text';
            nextElement.setSelectionRange(0, 0);
            nextElement.type = 'number';
        };

        return (
            <div className="multi-digit-input">
                <fieldset>
                    <legend>{label}</legend>
                    <ul>
                        {itemEls.map((item, index) => {
                            return (
                                <li key={item.id}>
                                    <label
                                        htmlFor={item.id}
                                        className="visually-hidden"
                                    >
                                        {item.label}
                                    </label>
                                    <input
                                        id={item.id}
                                        value={item.value}
                                        onChange={(event) => update(index, event)}
                                        onKeyDown={(event) => keyDown(index, event)}
                                        onPaste={(event) => handlePaste(event)}
                                        autoFocus={autofocus && index === 0}
                                        ref={index === 0 ? ref : null}
                                        disabled={disabled}
                                        type="number"
                                        maxLength="1"
                                        step="1"
                                        min="0"
                                        max="9"
                                    ></input>
                                </li>
                            );
                        })}
                    </ul>
                </fieldset>
            </div>
        );
    }
);

export default MultiDigitInput;
